import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileCode, Layers, Loader2, Plus, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { listPostmarkTemplates } from './api';
import type { PostmarkTemplate } from './types';

/**
 * Sentinel filter values. Real Postmark aliases can't collide with these
 * because Postmark aliases are restricted to letters/digits/hyphens.
 */
const NO_LAYOUT = '__none__';
const ONLY_LAYOUTS = '__layouts__';

export default function PostmarkTemplatesView() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PostmarkTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  /**
   * Selected layout filter:
   *   - `null`         → show all templates (standards + layouts)
   *   - `ONLY_LAYOUTS` → show only layout (base) templates
   *   - `NO_LAYOUT`    → show standard templates that don't reference a layout
   *   - any other str  → show standards whose `layoutTemplate` alias matches it
   */
  const [layoutFilter, setLayoutFilter] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Always include layouts so the filter bar can list them; the
      // `layoutFilter` state controls what actually renders below. Active
      // filtering is handled in Postmark's own UI, so we show everything.
      const list = await listPostmarkTemplates({ includeLayouts: true });
      setRows(list);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load templates';
      setError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Aliased layout templates, sorted by display name — these become filter chips. */
  const layouts = useMemo(
    () =>
      rows
        .filter((t) => t.templateType === 'Layout' && t.alias)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        ),
    [rows],
  );

  // If the currently selected layout is no longer in the list (e.g. after a
  // refresh removed it), fall back to "All" so the table isn't empty for a
  // ghost filter.
  useEffect(() => {
    if (
      layoutFilter === null ||
      layoutFilter === NO_LAYOUT ||
      layoutFilter === ONLY_LAYOUTS
    ) {
      return;
    }
    const stillExists = layouts.some((t) => t.alias === layoutFilter);
    if (!stillExists) setLayoutFilter(null);
  }, [layouts, layoutFilter]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((t) => {
      if (layoutFilter === ONLY_LAYOUTS) {
        if (t.templateType !== 'Layout') return false;
      } else if (layoutFilter === NO_LAYOUT) {
        if (t.templateType === 'Layout') return false;
        if (t.layoutTemplate) return false;
      } else if (layoutFilter !== null) {
        // Specific base: show only the standards that use this layout.
        // The layout itself is reachable via the "Layouts" chip.
        if (t.templateType === 'Layout') return false;
        if (t.layoutTemplate !== layoutFilter) return false;
      }
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        (t.alias?.toLowerCase().includes(q) ?? false) ||
        String(t.templateId).includes(q)
      );
    });
  }, [rows, searchQuery, layoutFilter]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <FileCode className="w-8 h-8 mr-3 text-emerald-500 shrink-0" />
            Postmark Templates
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage templates on your Postmark server. Used by automation steps and
            scheduled sends.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search…"
              className="pl-9 pr-3 py-2 w-48 sm:w-56 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center justify-center p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => navigate('/postmark-templates/new')}
            className="inline-flex items-center justify-center p-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
            title="New template"
            aria-label="New template"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mr-1">
            <Layers className="w-3.5 h-3.5" />
            Base
          </div>
          <FilterChip
            label="All"
            active={layoutFilter === null}
            onClick={() => setLayoutFilter(null)}
          />
          {layouts.length > 0 ? (
            <FilterChip
              label="Layouts"
              active={layoutFilter === ONLY_LAYOUTS}
              onClick={() => setLayoutFilter(ONLY_LAYOUTS)}
            />
          ) : null}
          {layouts.length > 0 ? (
            <div
              className="mx-1 h-5 w-px bg-gray-300 dark:bg-gray-600"
              aria-hidden="true"
            />
          ) : null}
          {layouts.map((layout) => (
            <FilterChip
              key={layout.templateId}
              label={layout.name || layout.alias || `Layout ${layout.templateId}`}
              active={layoutFilter === layout.alias}
              onClick={() => setLayoutFilter(layout.alias)}
            />
          ))}
          {layouts.length > 0 ? (
            <FilterChip
              label="No layout"
              active={layoutFilter === NO_LAYOUT}
              onClick={() => setLayoutFilter(NO_LAYOUT)}
              variant="muted"
            />
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="mb-4 p-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300 flex items-center justify-between gap-3">
          <span className="break-words">Could not load templates: {error}</span>
          <button
            onClick={() => {
              toast.dismiss();
              void load();
            }}
            className="shrink-0 px-2 py-1 text-xs font-medium rounded border border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/40"
          >
            Retry
          </button>
        </div>
      ) : null}

      {loading && rows.length === 0 ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <FileCode className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            {searchQuery
              ? 'No matching templates'
              : rows.length === 0
                ? 'No templates yet on this Postmark server'
                : 'Nothing matches your filters'}
          </h3>
          {!searchQuery && rows.length === 0 ? (
            <button
              onClick={() => navigate('/postmark-templates/new')}
              className="mt-4 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Create your first template
            </button>
          ) : null}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Alias
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filtered.map((t) => (
                  <tr
                    key={t.templateId}
                    onClick={() => navigate(`/postmark-templates/${t.templateId}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/postmark-templates/${t.templateId}`);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/30 focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-900/30 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t.name || '(unnamed)'}
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${
                          t.templateType === 'Layout'
                            ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200'
                            : 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200'
                        }`}
                      >
                        {t.templateType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 font-mono">
                      {t.alias ?? <span className="text-gray-400 italic">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200 font-mono">
                      {t.templateId}
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      {t.active ? (
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                          active
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          inactive
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
  variant?: 'default' | 'muted';
}

function FilterChip({ label, active, onClick, variant = 'default' }: FilterChipProps) {
  const base =
    'inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border transition-colors max-w-[16rem] truncate';
  let cls: string;
  if (active) {
    cls = 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600';
  } else if (variant === 'muted') {
    cls =
      'bg-transparent text-gray-500 dark:text-gray-400 border-dashed border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700';
  } else {
    cls =
      'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700';
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${cls}`}
      title={label}
    >
      <span className="truncate">{label}</span>
    </button>
  );
}
